import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, gql } from '@apollo/client';
import {
  WINDOW_TYPES,
  SOFT_GOODS_TYPES,
  FURNITURE_TYPES,
  BENCH_TYPES,
  CORNICE_TYPES,
  BED_TYPES,
  REUPHOLSTER_BENCH_TYPES,
  REUPHOLSTER_OTTOMAN_TYPES,
  REUPHOLSTER_POP_SEAT_TYPES,
  REUPHOLSTER_DINING_CHAIR_TYPES,
  PIN_BOARD_TYPES,
  ALL_TYPES,
} from './DraftQuoteObjects';
import QuoteSopDialog from './QuoteSopDialog';

// Which item types actually have an SOP row, so the ones without can be shown
// muted. Purely cosmetic — the dialog itself handles "no SOP loaded" — so a
// failed query just falls back to showing every link normally.
const SOP_TYPES_QUERY = gql`
  query QuoteSopTypes {
    allQuoteSops {
      nodes {
        itemType
      }
    }
  }
`;

// Same grouping as the Product Type dropdown in DraftQuoteDetail.tsx.
const GROUPS: { title: string; types: { value: string; label: string }[] }[] = [
  { title: 'Window Treatments', types: WINDOW_TYPES },
  { title: 'Soft Goods', types: SOFT_GOODS_TYPES },
  { title: 'Furniture', types: FURNITURE_TYPES },
  { title: 'Benches', types: BENCH_TYPES },
  { title: 'Cornices', types: CORNICE_TYPES },
  { title: 'Beds', types: BED_TYPES },
  {
    title: 'Reupholster',
    types: [
      ...REUPHOLSTER_BENCH_TYPES,
      ...REUPHOLSTER_OTTOMAN_TYPES,
      ...REUPHOLSTER_POP_SEAT_TYPES,
      ...REUPHOLSTER_DINING_CHAIR_TYPES,
    ],
  },
  { title: 'Pin Boards', types: PIN_BOARD_TYPES },
];

const s = {
  intro: { fontSize: 14, lineHeight: 1.6, color: '#374151', margin: '0 0 20px' } as React.CSSProperties,
  group: { marginBottom: 24 } as React.CSSProperties,
  groupTitle: {
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    color: '#6b7280',
    margin: '0 0 10px',
  },
  grid: { display: 'flex', flexWrap: 'wrap' as const, gap: 10 },
  link: (muted: boolean): React.CSSProperties => ({
    display: 'inline-block',
    padding: '9px 14px',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    background: '#fff',
    color: muted ? '#9ca3af' : '#2563eb',
    fontSize: 14,
    fontWeight: 500,
    textDecoration: 'none',
  }),
};

// The Item SOPs tab of the Docs page. Which SOP is open lives in the URL as
// ?tab=sops&sop=<item_type>, so a single SOP can be linked to directly (by
// people or bots) without a separate route.
export default function ItemSopsPanel() {
  const [params, setParams] = useSearchParams();
  const openType = params.get('sop') || '';
  const { data } = useQuery(SOP_TYPES_QUERY, { fetchPolicy: 'cache-and-network' });

  const withSop: Set<string> | null = data?.allQuoteSops?.nodes
    ? new Set<string>(data.allQuoteSops.nodes.map((n: any) => String(n.itemType).toLowerCase()))
    : null;

  const closeDialog = () => {
    const next = new URLSearchParams(params);
    next.delete('sop');
    setParams(next, { replace: true });
  };

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, marginTop: 0, marginBottom: 8, color: '#111827' }}>Item SOPs</h2>
      <p style={s.intro}>
        The written quoting procedure for each item type. Click one to read it and add shop clarifications.
        Greyed-out items don't have an SOP yet.
      </p>

      {GROUPS.map((g) => (
        <div key={g.title} style={s.group}>
          <h3 style={s.groupTitle}>{g.title}</h3>
          <div style={s.grid}>
            {g.types.map((t) => (
              <Link
                key={t.value}
                to={`/documentation?tab=sops&sop=${t.value}`}
                style={s.link(withSop !== null && !withSop.has(t.value))}
              >
                {t.label}
              </Link>
            ))}
          </div>
        </div>
      ))}

      <QuoteSopDialog
        open={!!openType}
        onClose={closeDialog}
        itemType={openType}
        itemTypeLabel={ALL_TYPES.find((t) => t.value === openType)?.label}
      />
    </div>
  );
}
